import { addDays, differenceInCalendarDays, format, parseISO, startOfDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { formatToBRL } from '@cannect/utils/formatters'
import { RECURRENCY_MVP_SETTINGS } from './constants'
import type {
  EligibilityView,
  PendingDocument,
  ReasonLists,
  RecurrencyReason,
  SubscriptionDetail,
  SubscriptionView
} from './types'

const GENERIC_REASON = 'Este item não pode entrar na Compra Recorrente agora'

// Corpo de recorrência do carrinho logado (research R5): número liga o ciclo,
// null desliga explicitamente, undefined não mexe (o serviço mantém o atual).
export const buildCartRecurrencyPayload = (cycleDays: number | null | undefined) => {
  if (typeof cycleDays === 'number') return { recurrency: { cycleDays } }
  if (cycleDays === null) return { recurrency: null }
  return {}
}

// A API manda códigos e textos em listas paralelas.
export const zipReasons = ({ reasons = [], messages = [] }: ReasonLists): RecurrencyReason[] =>
  reasons.map((code, index) => ({ code, message: messages[index] || GENERIC_REASON }))

const PRESCRIPTION_CODES = [
  'NO_VALID_PRESCRIPTION',
  'PRESCRIPTION_EXPIRES_BEFORE_DELIVERY',
  'PRESCRIPTION_UNSTRUCTURED',
  'PRODUCT_NOT_PRESCRIBED',
  'QUANTITY_ABOVE_PRESCRIPTION'
]
const ANVISA_CODES = ['NO_VALID_ANVISA', 'ANVISA_EXPIRES_BEFORE_DELIVERY']

export type ReasonAction = 'send_prescription' | 'schedule' | 'anvisa' | null

// O código só escolhe a ação; o texto exibido é sempre o do serviço.
export const reasonToAction = (
  code: string,
  { prescriberIsCannect }: { prescriberIsCannect?: boolean | null } = {}
) => {
  if (ANVISA_CODES.includes(code)) return 'anvisa' as ReasonAction
  if (PRESCRIPTION_CODES.includes(code)) {
    return (prescriberIsCannect ? 'schedule' : 'send_prescription') as ReasonAction
  }
  return null as ReasonAction
}

// 009: o portal só exibe a pendência que a API informa (FR-018).
export const hasDocumentsPending = (view?: Pick<EligibilityView, 'documentsPending'> | null) =>
  view?.documentsPending === true

// Texto do que falta enviar (research R4); lista vazia/ausente → genérico.
export const pendingDocumentsLabel = (docs?: PendingDocument[] | null) => {
  const list = docs || []
  const hasPrescription = list.includes('prescription')
  const hasAnvisa = list.includes('anvisa')
  if (hasPrescription && hasAnvisa) return 'sua receita e autorização Anvisa'
  if (hasPrescription) return 'sua receita'
  if (hasAnvisa) return 'sua autorização Anvisa'
  return 'seus documentos'
}

export type ProductRecurrencyState = 'hidden' | 'unavailable' | 'available'

// Estado do produto na PDP/vitrine (data-model §3). `NOT_IN_PILOT` vem nos
// motivos do pedido; receita e quantidade não escondem o produto (o carrinho
// explica esses motivos).
export const productRecurrencyState = (
  view: Pick<EligibilityView, 'reasons' | 'items'> | null | undefined,
  productId: number
): ProductRecurrencyState => {
  if (!view || (view.reasons || []).includes('NOT_IN_PILOT')) return 'hidden'
  const item = view.items.find((i) => Number(i.productId) === Number(productId))
  if (!item) return 'hidden'
  const reasons = item.reasons || []
  if (
    reasons.includes('PRODUCT_NOT_ENABLED') ||
    reasons.includes('PRODUCT_NOT_FOUND') ||
    reasons.includes('NOT_IN_PILOT')
  ) {
    return 'hidden'
  }
  if (reasons.includes('PRODUCT_UNAVAILABLE')) return 'unavailable'
  return 'available'
}

// `YYYY-MM-DD` lido como data local (sem conversão de fuso).
export const formatApiDate = (value?: string | null, pattern = 'dd/MM/yyyy') => {
  if (!value) return ''
  return format(parseISO(value.slice(0, 10)), pattern, { locale: ptBR })
}

// ISO com hora (histórico) sempre no fuso de São Paulo, independente do navegador.
export const formatApiDateTime = (iso?: string | null, pattern = 'dd/MM/yyyy HH:mm') => {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date)
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return pattern
    .replace('dd', part('day'))
    .replace('MM', part('month'))
    .replace('yyyy', part('year'))
    .replace('HH', part('hour'))
    .replace('mm', part('minute'))
}

const parseApiDate = (value: string) => parseISO(value.slice(0, 10))

// Dias de calendário entre hoje e uma data `YYYY-MM-DD` (negativo = passou).
export const daysUntil = (value: string, today: Date = new Date()) =>
  differenceInCalendarDays(parseApiDate(value), startOfDay(today))

// Única data derivada no portal (research R9), só para o aviso de pausa longa,
// até a API expor `pauseEndsAt` (delta D6).
export const pauseEndsAt = (sub: Pick<SubscriptionView, 'status' | 'pausedAt'>) => {
  if (sub.status !== 'paused' || !sub.pausedAt) return null
  return addDays(parseApiDate(sub.pausedAt), RECURRENCY_MVP_SETTINGS.maxPauseDays)
}

export const PAUSE_ENDING_SOON_DAYS = 10

export const isPauseEndingSoon = (sub: Pick<SubscriptionView, 'status' | 'pausedAt'>, today: Date = new Date()) => {
  const end = pauseEndsAt(sub)
  if (!end) return false
  return differenceInCalendarDays(end, startOfDay(today)) <= PAUSE_ENDING_SOON_DAYS
}

export type SubscriptionDisplayKind =
  | 'cancelled'
  | 'ended'
  | 'paused_payment'
  | 'paused_document'
  | 'paused_patient'
  | 'paused_cannect'
  | 'documents_pending'
  | 'active'

export type SubscriptionDisplayState = {
  kind: SubscriptionDisplayKind
  chipLabel: string
  chipVariant: 'success' | 'warning' | 'danger' | 'neutral'
  sentence: string
}

const pendingDocumentsSentence = (docs?: Array<'prescription' | 'anvisa'>) => {
  const list = docs || []
  if (list.length === 1 && list[0] === 'prescription') return 'Aguardando sua receita'
  if (list.length === 1 && list[0] === 'anvisa') return 'Aguardando sua autorização Anvisa'
  return 'Aguardando seus documentos'
}

// Estado de exibição (data-model §5), em ordem de precedência. Sem regra de negócio.
export const getSubscriptionDisplayState = (sub: SubscriptionView): SubscriptionDisplayState => {
  if (sub.status === 'cancelled') {
    return { kind: 'cancelled', chipLabel: 'Cancelada', chipVariant: 'neutral', sentence: 'Cancelada' }
  }
  if (sub.status === 'ended') {
    return { kind: 'ended', chipLabel: 'Encerrada', chipVariant: 'neutral', sentence: 'Encerrada' }
  }
  if (sub.status === 'paused') {
    if (sub.pauseReason === 'payment') {
      return {
        kind: 'paused_payment',
        chipLabel: 'Pausada',
        chipVariant: 'danger',
        sentence: 'Pausada: não conseguimos cobrar seu cartão. Troque o cartão.'
      }
    }
    if (sub.pauseReason === 'document') {
      return {
        kind: 'paused_document',
        chipLabel: 'Pausada',
        chipVariant: 'danger',
        sentence: 'Pausada: sua receita venceu. Envie uma nova para voltar.'
      }
    }
    if (sub.pauseReason === 'patient') {
      return {
        kind: 'paused_patient',
        chipLabel: 'Pausada',
        chipVariant: 'warning',
        sentence: sub.pausedUntil
          ? `Pausada até ${formatApiDate(sub.pausedUntil, 'dd/MM')}. Volta sozinha nessa data.`
          : 'Pausada por você.'
      }
    }
    return {
      kind: 'paused_cannect',
      chipLabel: 'Pausada',
      chipVariant: 'warning',
      sentence: 'Pausada pela Cannect. Fale com o acolhimento.'
    }
  }
  if (sub.documentsPending) {
    return {
      kind: 'documents_pending',
      chipLabel: 'Aguardando documentos',
      chipVariant: 'warning',
      sentence: pendingDocumentsSentence(sub.pendingDocuments)
    }
  }
  const amount =
    sub.estimatedAmount !== null && sub.estimatedAmount !== undefined ? formatToBRL(sub.estimatedAmount) : ''
  const date = sub.nextChargeAt
    ? `Próxima cobrança em ${formatApiDate(sub.nextChargeAt, 'dd/MM')}`
    : 'Próxima cobrança a definir'
  return {
    kind: 'active',
    chipLabel: 'Ativa',
    chipVariant: 'success',
    sentence: amount ? `${date} · ${amount}` : date
  }
}

export type SubscriptionAction = 'skip' | 'postpone' | 'pause' | 'resume' | 'cancel' | 'edit' | 'card'

export const BLOCKED_REASON = {
  processing: 'Este ciclo já está em processamento',
  editProcessing: 'Este ciclo já está em processamento; alterações valem para o próximo',
  alreadyPostponed: 'Você já adiou este ciclo',
  paused: 'Retome a assinatura para usar esta opção',
  inactive: 'Esta assinatura não está mais ativa',
  generic: 'Esta opção não está disponível agora'
}

export const canDo = (detail: SubscriptionDetail, action: SubscriptionAction) => {
  switch (action) {
    case 'skip':
      return detail.canSkip
    case 'postpone':
      return detail.canPostpone
    case 'pause':
      return detail.canPause
    case 'resume':
      return detail.canResume
    case 'cancel':
      return detail.canCancel
    case 'edit':
      return detail.canEdit
    case 'card':
      // Trocar cartão pausada retoma a pausa por pagamento (contrato da API).
      return detail.status === 'paused' || (detail.status === 'active' && detail.canEdit)
    default:
      return false
  }
}

const currentCycleView = (detail: SubscriptionDetail) =>
  detail.cycles?.find((cycle) =>
    detail.currentCycle?.id ? cycle.id === detail.currentCycle.id : cycle.number === detail.currentCycle?.number
  )

// Motivo exibido quando a ação está bloqueada (research R9). A permissão em si
// vem sempre de `can*`; aqui só se escolhe o texto.
export const getBlockedReason = (detail: SubscriptionDetail, action: SubscriptionAction): string | null => {
  if (canDo(detail, action)) return null
  if (detail.status === 'cancelled' || detail.status === 'ended') return BLOCKED_REASON.inactive
  if (detail.status === 'paused' && action !== 'resume' && action !== 'cancel') return BLOCKED_REASON.paused
  const cycleStatus = detail.currentCycle?.status
  if (cycleStatus === 'locked' || cycleStatus === 'pending_payment') {
    return action === 'edit' || action === 'card' ? BLOCKED_REASON.editProcessing : BLOCKED_REASON.processing
  }
  if (action === 'postpone' && currentCycleView(detail)?.postponedAt) return BLOCKED_REASON.alreadyPostponed
  if ((action === 'edit' || action === 'card' || action === 'skip' || action === 'postpone') && !detail.canEdit) {
    return action === 'edit' || action === 'card' ? BLOCKED_REASON.editProcessing : BLOCKED_REASON.processing
  }
  return BLOCKED_REASON.generic
}

export const GENERIC_ERROR_MESSAGE = 'Não foi possível concluir agora. Tente de novo em instantes.'

export type RecurrencyErrorInfo = {
  status?: number
  code?: string
  message: string
  reasons: string[]
  messages: string[]
  partial: boolean
  items: Array<{ productId: number; allowedQuantityPerCycle?: number | null }>
}

export const getRecurrencyError = (err: unknown): RecurrencyErrorInfo => {
  const response = (err as any)?.response
  const data = response?.data || {}
  return {
    status: response?.status,
    code: typeof data.code === 'string' ? data.code : undefined,
    message: (typeof data.message === 'string' && data.message) || GENERIC_ERROR_MESSAGE,
    reasons: Array.isArray(data.reasons) ? data.reasons : [],
    messages: Array.isArray(data.messages) ? data.messages : [],
    partial: Boolean(data.partial),
    items: Array.isArray(data.items) ? data.items : []
  }
}

export const CANCEL_REASON_MAX = 500
export const CANCEL_REASON_SEPARATOR = ' — '

// Texto legível que vai para a API (o motivo é livre no servidor, até 500).
export const buildCancelReason = (label?: string | null, comment?: string | null) => {
  const parts = [label?.trim(), comment?.trim()].filter(Boolean) as string[]
  if (!parts.length) return undefined
  return parts.join(CANCEL_REASON_SEPARATOR).slice(0, CANCEL_REASON_MAX)
}

const isOpen = (sub: Pick<SubscriptionView, 'status'>) => sub.status === 'active' || sub.status === 'paused'

export const getPaymentAlert = (sub: SubscriptionView | SubscriptionDetail) =>
  isOpen(sub) && (sub.pauseReason === 'payment' || (sub as SubscriptionDetail).cycles?.[0]?.status === 'failed')

export const getDocumentAlert = (sub: SubscriptionView) => {
  if (!isOpen(sub)) return false
  if (sub.documentsPending || sub.pauseReason === 'document') return true
  const docs = [sub.documents?.prescription, sub.documents?.anvisa].filter(Boolean)
  return docs.some((doc) => doc?.status === 'expired' || doc?.status === 'none' || doc?.expiringSoon)
}
